import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import PublicShell, { useSiteLang } from "@/components/PublicShell";
import { api } from "@/lib/api";
import OrderReceipt from "@/components/OrderReceipt";

const P = {
  en: { home: "Back to home", checking: "Confirming payment…", wait: "Please wait a moment.", paidT: "Thank you — payment received",
    paid: "Thank you! Your payment was received and your volunteer codes are being issued now. They'll arrive by email in a few minutes, as a list and an attached PDF.",
    pendT: "Payment is processing", pend: "We'll email you once your payment is confirmed and your codes are issued.", errT: "We couldn't confirm payment", err: "Please contact us if you were charged.",
    cancelT: "Payment cancelled", cancelA: "No charge was made. You can", cancelL: "start your order again", cancelB: "anytime." },
  es: { home: "Volver al inicio", checking: "Confirmando el pago…", wait: "Espere un momento, por favor.", paidT: "Gracias, recibimos su pago",
    paid: "¡Gracias! Recibimos su pago y estamos emitiendo sus códigos para voluntarios. Le llegarán por correo electrónico en unos minutos, como lista y en un PDF adjunto.",
    pendT: "El pago se está procesando", pend: "Le enviaremos un correo cuando se confirme su pago y se emitan sus códigos.", errT: "No pudimos confirmar el pago", err: "Comuníquese con nosotros si se le hizo un cargo.",
    cancelT: "Pago cancelado", cancelA: "No se hizo ningún cargo. Puede", cancelL: "comenzar su pedido de nuevo", cancelB: "cuando quiera." },
};

function Panel({ icon, title, children, testId }: { icon: React.ReactNode; title: string; children: React.ReactNode; testId: string }) {
  const t = P[useSiteLang()];
  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-4 pt-20">
        <div className="card-soft fade-up p-10 text-center" data-testid={testId}>
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-[#F1EFEA]">{icon}</div>
          <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{title}</h1>
          <div className="mt-4 text-lg text-[#475569]">{children}</div>
          <Link to="/" className="btn-secondary mt-8" data-testid="payment-home-link">{t.home}</Link>
        </div>
      </div>
    </PublicShell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState<"checking" | "paid" | "pending" | "error">("checking");
  const t = P[useSiteLang()];

  useEffect(() => {
    if (!sid) return setState("error");
    let tries = 0;
    let timer: any;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch {
        return setState("error");
      }
      if (++tries >= 10) return setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);

  if (state === "checking") return <Panel testId="payment-checking" icon={<Loader2 className="h-8 w-8 animate-spin text-[#1B365D]" />} title={t.checking}>{t.wait}</Panel>;
  if (state === "paid")
    return (
      <OrderReceipt sid={sid!} fallback={<Panel testId="payment-success" icon={<CheckCircle2 className="h-8 w-8 text-[#15803D]" />} title={t.paidT}>
        {t.paid}
      </Panel>} />
    );
  if (state === "pending") return <Panel testId="payment-pending" icon={<Clock className="h-8 w-8 text-[#B45309]" />} title={t.pendT}>{t.pend}</Panel>;
  return <Panel testId="payment-error" icon={<XCircle className="h-8 w-8 text-[#B91C1C]" />} title={t.errT}>{t.err}</Panel>;
}

export function PaymentCancel() {
  const t = P[useSiteLang()];
  return (
    <Panel testId="payment-cancelled" icon={<XCircle className="h-8 w-8 text-[#64748B]" />} title={t.cancelT}>
      {t.cancelA} <Link to="/order" className="font-semibold text-[#1B365D] underline">{t.cancelL}</Link> {t.cancelB}
    </Panel>
  );
}
